import React, {useMemo} from 'react';
import {convertToCustomElement} from '../formbird/CustomElementWrapper';
import {useFormbird} from '../formbird/FormbirdContext';
import {
    Box,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip as MuiChip
} from '@mui/material';
import type { ChipProps } from '@mui/material/Chip'
import { styled } from '@mui/material/styles';
import { Kpi, KpiContract, KpiContractUnits, StringIndexedObject } from './utils/Types';
import {
    groupBy,
    getPoorRange,
    kpiCellColor,
    KpiCellColor,
    formatKpiContactValue,
    ColoredKPIValueChip
} from './utils/Misc';
import {useWatchedFields} from './utils/Hooks';
import { getWeek } from 'date-fns';
import {Link} from "./utils/Link";
import {changedDocumentService} from "../services";

const Chip = styled(MuiChip)<ChipProps>({
    fontSize: 'inherit'
})

interface KpiDay {
    dailyDate: string;
    dailyCaseCount: number;
    daily: Kpi;
    mtd: Kpi;
}

const Header = ({ kpi }: { kpi: KpiContract }) => {
    const code = kpi.code.replaceAll('_', '.');
    return <Box sx={{py: 2}}>
        <Typography variant='h5'>
            <Link underline='hover' href={`/form/${kpi.reportLinkDocId}`}>{code}</Link>: {kpi.description}
        </Typography>
        <Box sx={{display: 'flex', gap: 1, py: 2}}>
            <Chip label={`Units: ${kpi.units}`} />
            <ColoredKPIValueChip value={`Unsatisfactory: ${formatKpiContactValue(kpi.unsatisfactoryLimit, kpi.units)}`} color='red' />
            <ColoredKPIValueChip value={`Poor: ${getPoorRange(kpi.satisfactoryLimit, kpi.unsatisfactoryLimit, kpi.units) ?? 'N/A'}`} color='yellow' />
            <ColoredKPIValueChip value={`Satisfactory: ${formatKpiContactValue(kpi.satisfactoryLimit, kpi.units)}`} color='green' />
        </Box>
    </Box>;
};

type TableData = { date: Date; daily: number; mtd: number }[];

const Cell = (props: { value: number, cellColor: KpiCellColor, units: KpiContractUnits, onClick: () => void }) => {
    return (
        <TableCell align='center'>
            <ColoredKPIValueChip value={formatKpiContactValue(props.value, props.units)} color={props.cellColor} onClick={props.onClick} sx={{cursor: 'pointer'}} />
        </TableCell>
    )
}

const KPIDailyTable = (props: { data: TableData, kpi: KpiContract }) => {
    const fb = useFormbird<{}>();
    const grouped = groupBy(props.data, (it) => getWeek(it.date));

    const tables = [...grouped.entries()].map(([week, rows], index) => (
        <>
            <Table sx={{
                height: 'fit-content',
            }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Week {index + 1}</TableCell>
                        <TableCell align='center'>Daily</TableCell>
                        <TableCell align='center'>MTD</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const dailyColor = kpiCellColor(props.kpi.satisfactoryLimit, props.kpi.unsatisfactoryLimit, row.daily);
                        const mtdColor = kpiCellColor(props.kpi.satisfactoryLimit, props.kpi.unsatisfactoryLimit, row.mtd);
                        const onBodyCellClick = (dailyOrMtd: 'daily' | 'mtd') => {
                            let current, next;
                            if (dailyOrMtd == 'daily') {
                                current = new Date(row.date);
                                next = new Date(row.date);
                                next.setDate(row.date.getDate() + 1);
                            } else if (dailyOrMtd == 'mtd') {
                                current = new Date(row.date);
                                current.setDate(1);
                                next = new Date(row.date);
                                next.setDate(next.getDate() + 1);
                            }
                            changedDocumentService.valueChanged(fb, [
                                current.toISOString(),
                                next.toISOString(),
                            ], fb.registeredControlName)
                        }
                        return (
                            <TableRow key={row.date.getDate()}>
                                <TableCell onClick={() => onBodyCellClick('daily')} sx={{cursor: 'pointer'}}>{row.date.toDateString()}</TableCell>
                                <Cell value={row.daily} units={props.kpi.units} cellColor={dailyColor} onClick={() => onBodyCellClick('daily')} />
                                <Cell value={row.mtd} units={props.kpi.units} cellColor={mtdColor} onClick={() => onBodyCellClick('mtd')} />
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            <Divider orientation='vertical' sx={{height: 'unset'}}/>
        </>
    ));
    console.debug('kpiDailySummary', tables);

    return (
        <TableContainer component={Paper} sx={{display: 'flex'}}>
            {tables}
        </TableContainer>
    );
};

const useConfiguredData = (componentDefinition: StringIndexedObject) => {
    const fieldMapping = useMemo(() => {
        const getFieldName = (fieldName: string, defaultName: string): string => {
            const name = componentDefinition[fieldName] as string;
            return name ?? defaultName;
        };

        const dataField = getFieldName('dataField', 'kpiDaily');
        const mtdField = getFieldName('mtdField', 'monthToDate');
        const codeField = getFieldName('codeField', 'codeSelector');
        const kpiTargetsField = getFieldName('kpisField', 'kpiTargets');

        return {
            [dataField]: 'kpiDaily',
            [mtdField]: 'monthToDate',
            [codeField]: 'codeSelector',
            [kpiTargetsField]: 'kpiTargets',
        };
    }, [componentDefinition]);

    type Fields = { kpiDaily: KpiDay[], codeSelector: [string], monthToDate: string, kpiTargets: KpiContract[] };
    const {codeSelector = [], kpiTargets = [], kpiDaily = []} = useWatchedFields<Fields>(fieldMapping);
    const code = codeSelector[0];
    const kpi = kpiTargets.find(it => it.code === code) ?? kpiTargets[0];
    if (!code || !kpi) { return undefined }
    return {
        code,
        kpi,
        dailyData: kpiDaily.map((value) => ({
            date: new Date(value.dailyDate),
            daily: value.daily[code],
            mtd: value.mtd[code]
        }))
    };
};


const KPIDailySummary = () => {
    const {componentDefinition} = useFormbird();

    const data = useConfiguredData(componentDefinition);
    return data ? (
        <>
            <Typography variant='body1'>{componentDefinition.label}</Typography>
            <Header kpi={data.kpi}/>
            <KPIDailyTable data={data.dailyData} kpi={data.kpi}/>
        </>
    ) : <></>;
};

convertToCustomElement('cww-kpi-daily-summary', KPIDailySummary);
convertToCustomElement('gww-kpi-daily-summary', KPIDailySummary);

export default KPIDailySummary;
